import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'

const Login = () => {
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [step, setStep] = useState(1) // 1: Email/Password, 2: OTP
    const [email, setEmail] = useState('')
    const [isResendingOTP, setIsResendingOTP] = useState(false)
    const [otpSentTime, setOtpSentTime] = useState(null)
    const { login, setUser } = useAuth()
    const navigate = useNavigate()

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm()

    const onSubmit = async (data) => {
        console.log('=== FORM SUBMISSION STARTED ===');
        console.log('Step:', step);
        console.log('Form data:', { email: data.email, password: '***' });
        
        setIsLoading(true)
        setError('')
        setSuccess('')

        try {
            if (step === 1) {
                // Step 1: Send login request (may send OTP or direct login)
                console.log('Sending login request with data:', { email: data.email, password: '***' })
                console.log('API URL:', '/auth/login');
                
                console.log('Making API request to:', api.defaults.baseURL + '/auth/login');
                const response = await api.post('/auth/login', data)
                console.log('Login response:', response.data)
                
                // Check if this is a direct login (OTP disabled) or OTP flow
                if (response.data.data && response.data.data.token) {
                    // Direct login - OTP disabled for development
                    console.log('Direct login detected - OTP disabled')
                    
                    // Store the token and update auth context
                    localStorage.setItem('token', response.data.data.token)
                    setUser(response.data.data.user)
                    
                    // Redirect to camera monitoring
                    navigate('/camera-monitoring')
                } else {
                    // OTP flow - normal production behavior
                    console.log('OTP flow detected - waiting for OTP verification')
                    setEmail(data.email)
                    setSuccess(response.data.message)
                    setOtpSentTime(new Date())
                    setStep(2)
                    reset({ email: data.email })
                }
            } else if (step === 2) {
                // Step 2: Verify OTP
                console.log('Verifying OTP for email:', email, 'OTP:', data.otp)
                const response = await api.post('/auth/verify-otp', {
                    email: email,
                    otp: data.otp
                })
                console.log('OTP verification response:', response.data)

                // Store the token and update auth context
                localStorage.setItem('token', response.data.data.token)
                setUser(response.data.data.user)

                // Show registration notification if this is a new user
                if (response.data.data.isNewUser) {
                    setSuccess('Congratulations on your registration! Please wait for authentication.')
                    // Delay redirect to show the message
                    setTimeout(() => {
                        navigate('/camera-monitoring')
                    }, 3000)
                } else {
                    // Redirect to camera monitoring immediately for existing users
                    navigate('/camera-monitoring')
                }
            }
        } catch (error) {
            console.error('Login error:', error)
            console.error('Error type:', error.name)
            console.error('Error message:', error.message)
            console.error('Error response:', error.response?.data)
            console.error('Error status:', error.response?.status)
            console.error('Error config:', error.config)
            
            // Handle multiple login detection
            if (error.response?.data?.code === 'MULTIPLE_LOGIN_DETECTED') {
                setError('Multiple login attempt detected. Please log in again using OTP')
                setStep(2) // Force OTP step
                setEmail(data.email)
                setOtpSentTime(new Date())
                reset({ email: data.email })
            } else if (error.code === 'ERR_NETWORK') {
                setError('Network error. Please check your connection and try again.')
            } else if (error.response?.status === 404) {
                setError('API endpoint not found. Please contact support.')
            } else if (error.response?.status === 500) {
                setError('Server error. Please try again later.')
            } else {
                setError(error.response?.data?.message || 'Login failed. Please try again.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleResendOTP = async () => {
        setIsResendingOTP(true)
        setError('')
        
        try {
            console.log('Resending OTP to:', email)
            const response = await api.post('/auth/resend-otp', { email })
            console.log('Resend OTP response:', response.data)
            
            setSuccess('OTP has been resent to your email')
            setOtpSentTime(new Date())
        } catch (error) {
            console.error('Resend OTP error:', error)
            setError(error.response?.data?.message || 'Failed to resend OTP. Please try again.')
        } finally {
            setIsResendingOTP(false)
        }
    }



    return (
        <div className="min-h-screen flex">
            {/* Left side - Brand section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-700 via-indigo-800 to-cyan-700 p-8">
                <div className="flex flex-col justify-center items-center text-center text-white w-full">
                    <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-20 mb-4" />
                    <p className="text-lg text-cyan-200 mb-4">SHAR Projects Pvt. Ltd</p>
                    <p className="text-xl text-cyan-100 font-medium">Camera Management System</p>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100 py-8 px-4">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex flex-col items-center mb-8 lg:hidden text-center">
                        <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-14 mb-2" />
                        <p className="text-sm text-gray-600">SHAR Projects Pvt. Ltd</p>
                        <p className="text-sm text-gray-500">Camera Management System</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {step === 1 ? 'Welcome back' : 'Enter OTP'}
                            </h2>
                            <p className="text-gray-600">
                                {step === 1 ? 'Sign in to your account' : 'Enter the 6-digit OTP sent to your email'}
                            </p>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Success message */}
                        {success && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-600">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {step === 1 && (
                                <>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Email address
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                id="email"
                                                type="email"
                                                autoComplete="email"
                                                className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                                placeholder="Enter your email"
                                                {...register('email', {
                                                    required: 'Email is required',
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: 'Invalid email address'
                                                    }
                                                })}
                                            />
                                        </div>
                                        {errors.email && (
                                            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                className="input pl-10 pr-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                                placeholder="Enter your password"
                                                {...register('password', {
                                                    required: 'Password is required'
                                                })}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => setShowPassword(!showPassword)}
                                                tabIndex={-1}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {step === 2 && (
                                <div>
                                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                                        OTP Code
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            id="otp"
                                            type="text"
                                            maxLength="6"
                                            className="input pl-10 text-center text-2xl font-mono focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="000000"
                                            {...register('otp', {
                                                required: 'OTP is required',
                                                pattern: {
                                                    value: /^[0-9]{6}$/,
                                                    message: 'OTP must be 6 digits'
                                                }
                                            })}
                                        />
                                    </div>
                                    {errors.otp && (
                                        <p className="mt-1 text-sm text-red-600">{errors.otp.message}</p>
                                    )}
                                </div>
                            )}

                            {step === 1 && (
                                <div className="flex items-center justify-between">
                                    <div></div>
                                    <Link
                                        to="/forgot-password"
                                        className="text-sm text-cyan-600 hover:text-cyan-500 transition"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-3">
                                    <div className="text-center">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setStep(1)
                                                setError('')
                                                setSuccess('')
                                            }}
                                            className="text-sm text-cyan-600 hover:text-cyan-500 transition"
                                        >
                                            ← Back to login
                                        </button>
                                    </div>
                                    
                                    <div className="text-center">
                                        <button
                                            type="button"
                                            onClick={handleResendOTP}
                                            disabled={isResendingOTP}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-cyan-600 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100 hover:border-cyan-300 hover:text-cyan-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                                        >
                                            {isResendingOTP ? (
                                                <span className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyan-600 mr-2"></div>
                                                    Resending...
                                                </span>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Didn't receive OTP? Resend
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {otpSentTime && (
                                        <div className="text-center text-xs text-gray-500">
                                            OTP sent at: {otpSentTime.toLocaleTimeString()}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {step === 1 ? 'Sending OTP...' : 'Verifying OTP...'}
                                        </div>
                                    ) : (
                                        <>
                                            {step === 1 ? 'Sign in' : 'Verify OTP'}
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="text-center">
                                <p className="text-sm text-gray-600">
                                    Dont have an account?{' '}
                                    <Link
                                        to="/signup"
                                        className="font-medium text-cyan-600 hover:text-cyan-500 transition"
                                    >
                                        Sign up here
                                    </Link>
                                </p>
                            </div>

                            <div className="text-center pt-4 border-t border-gray-200">
                                <div className="flex justify-center space-x-6">
                                    <Link
                                        to="/support"
                                        className="text-sm text-gray-500 hover:text-gray-700 transition"
                                    >
                                        Support
                                    </Link>
                                    <Link
                                        to="/privacy-policy"
                                        className="text-sm text-gray-500 hover:text-gray-700 transition"
                                    >
                                        Privacy Policy
                                    </Link>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Login 